import config from '@/config/config';
import {
  createPathPrefixMatcher,
  createSessionReplayGate,
  exposeSessionReplayDebugHandle,
  attemptSessionReplayStart
} from '@satellite/analytics/sessionReplay';
import type {
  SessionReplayRecorder,
  SessionReplayGateContext,
  SessionReplayGate
} from '@satellite/analytics/sessionReplay';

// ---------------------------------------------------------------------------
// Gate setup — module singleton, one instance per page load.
// routes.js is plain JS outside the Vue injection tree; a singleton is the
// pragmatic seam (same pattern as utils.js isEnabled()).
// ---------------------------------------------------------------------------

const isEligiblePath = createPathPrefixMatcher('/yard');

const gate: SessionReplayGate = createSessionReplayGate({
  // The stored object carries its own schema version (GATE_STATE_VERSION); the key does not.
  storageKey: 'od:session_replay_gate:helios',
  enabled: () => Boolean(config.mixpanel_token) && Boolean(config.mixpanel_session_replay_enabled),
  samplePercent: () => config.mixpanel_session_replay_sample_percent ?? 0,
  isEligiblePath,
  debug: config.server_env !== 'production'
});

let _recorder: SessionReplayRecorder | null = null;

/**
 * Register the analytics client as the Session Replay recorder and expose
 * the debug handle on `window.__odSessionReplay`.
 *
 * Must be called before the first router navigation (i.e. from
 * createHeliosAnalyticsClient in useHeliosAnalytics.ts).
 */
export function registerHeliosSessionReplayRecorder(recorder: SessionReplayRecorder): void {
  _recorder = recorder;
  exposeSessionReplayDebugHandle('__odSessionReplay', {
    gate,
    recorder,
    allowForceStart: config.server_env !== 'production'
  });
}

/**
 * Evaluate the gate for the given navigation context and start the replay if
 * eligible. Safe to call on every afterEach — short-circuits immediately for
 * non-eligible outcomes. Never throws.
 *
 * No-ops silently if no recorder has been registered (no token → Mixpanel
 * disabled → recorder was never wired).
 */
export function maybeStartHeliosSessionReplay(context: SessionReplayGateContext): void {
  if (!_recorder) {
    return;
  }
  attemptSessionReplayStart({ gate, recorder: _recorder, context });
}

/** Exposed for unit tests only. */
export const heliosSessionReplayGate = gate;
